import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import { confirmDialog } from 'primereact/confirmdialog';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import '../../../assets/css/style.css';
import '../../../assets/css/iconsmenu.css';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import UtilContext from '../../../components/UtilContext';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import { downloadBlobGet, fetchTextGet, getRequest, postRequest } from '../../../api/ApiController';
import { createArrayNavigateHandlers } from '../../../utils/arrayNavigate';
import { getTimeUnitDays } from '../../../utils/moduleSettings';
import { cleanSorters } from '../../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../../utils/tabulatorPersistLayout';
import PrintOptionsDialog from '../../../components/PrintOptionsDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../../utils/serviceTaskExport';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../../utils/exportExcelNaming';
import { primeMultiSelectHeaderFilter } from '../../../utils/primeMultiSelectHeaderFilter';
import TabulatorMaskDialog from '../../../utils/tabulatorMaskDialog';
import { LocaleContext } from '../../../components/LocaleContext';

import {
  SaveIcon, SaveAndCloseIcon, AddIcon, BackToFirstIcon,
  BackIcon, ForwardIcon, ForwardToLastIcon, VerticalLineIcon, DeleteIcon,
  FavoriteIcon, PriceIcon, EditIcon, EditRowIcon, PrintIcon, ExportIcon, RefreshIcon,
} from '../../svg/svgIcons';

const PAGE_MASK = 'TaskTemplateAdd';

/** Stĺpce zoznamu šablón pre serverovú tlač / XLSX z detailu (only_id). */
const LIST_EXPORT_COLUMN_FIELDS = ['id', 'name', 'rows_count', 'total_duration', 'created_at', 'updated_at'];

const DEFAULT_ROW_COLUMNS_VISIBLE = Object.freeze({
  id: true,
  title: true,
  assignee_id: true,
  duration_days: true,
  chain_mode: true,
});

function applyRowDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(visibilityMap || DEFAULT_ROW_COLUMNS_VISIBLE)) {
    if (visibilityMap[f] === false) {
      try {
        table.setHeaderFilterValue?.(f, '', true);
      } catch (_) {}
      table.hideColumn?.(f);
    } else {
      table.showColumn?.(f);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

/** Detail šablóny: nová šablóna sa vytvára v TaskTemplateList (POST + tab edit); sem sa vstupuje s template + type edit. Kopírovanie: type add + template z výberu. */
export default function TaskTemplateAdd({ template = null, type = 'add', templateContext = 'service' }) {
  const { t } = useContext(LocaleContext);
  const [state, dispatch] = useContext(DatabaseContext);
  const {
    toast,
    setVisible,
    setDialogText,
    openFavoriteDialog,
    setFastView,
    setUserMaskFilter,
    openChosenNotificationPreferences,
  } = useContext(UtilContext);

  const [formType, setFormType] = useState(type);
  const [formTemplate, setFormTemplate] = useState(template);
  const [closeTab, setCloseTab] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const [editRowDisabled, setEditRowDisabled] = useState(true);

  const [rows, setRows] = useState([]);
  const [rowDialogVisible, setRowDialogVisible] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);

  const [rowColumnsVisible, setRowColumnsVisible] = useState({ ...DEFAULT_ROW_COLUMNS_VISIBLE });
  const [maskVisible, setMaskVisible] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');
  const normalizedContext = templateContext === 'crm' || formTemplate?.template_context === 'crm' ? 'crm' : 'service';
  const isCrmContext = normalizedContext === 'crm';
  const pageMask = isCrmContext ? 'CustomerTaskTemplateAdd' : PAGE_MASK;
  const rowColumnDefaults = useMemo(
    () => (isCrmContext ? { ...DEFAULT_ROW_COLUMNS_VISIBLE, crm_step_id: true } : { ...DEFAULT_ROW_COLUMNS_VISIBLE }),
    [isCrmContext]
  );

  const tableRef = useRef(null);
  const submitRef = useRef(null);
  const overlayRef = useRef(null);
  const isRestoringRef = useRef(false);

  const renumberRows = (list) => (list || []).map((r, idx) => ({ ...r, order_index: idx + 1 }));

  const usersOptions = useMemo(() => {
    const activeUsers = (state.users || []).filter(
      (u) => !u?.employee?.date_of_departure
    );
    return activeUsers.map((u) => ({
      value: u.id,
      label: u?.employee?.name || u?.username,
    }));
  }, [state.users]);
  const crmStepOptions = useMemo(() => (
    (state.crm_steps || []).map((step) => ({
      value: step.id,
      label: step?.name?.sk || step?.name?.en || step?.name?.de || step?.name || `#${step.id}`,
    }))
  ), [state.crm_steps]);

  const rowFormik = useFormik({
    initialValues: {
      title: '',
      assignee_id: '',
      crm_step_id: '',
      duration_days: 0,
      chain_mode: 'sequential',
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      if (!(values.title || '').toString().trim()) errors.title = t('Common.RequiredField');
      const d = Number(values.duration_days);
      if (!Number.isFinite(d) || d < 0) errors.duration_days = t('TaskTemplateAdd.NonNegativeNumber');
      return errors;
    },
    onSubmit: (values, { resetForm }) => {
      const title = (values.title || '').trim();
      const assigneeId = values.assignee_id === '' || values.assignee_id == null ? null : Number(values.assignee_id);
      const crmStepId = values.crm_step_id === '' || values.crm_step_id == null ? null : Number(values.crm_step_id);
      const duration_days = Number.isFinite(+values.duration_days) ? Math.max(0, Math.floor(+values.duration_days)) : 0;
      const willBeFirst = editingRowId
        ? rows.find((r) => r.id === editingRowId)?.order_index === 1
        : rows.length === 0;
      const chain_mode = willBeFirst
        ? 'sequential'
        : (values.chain_mode === 'parallel_prev' ? 'parallel_prev' : 'sequential');

      if (editingRowId) {
        setRows((prev) => {
          const next = prev.map((r) =>
            r.id === editingRowId
              ? { ...r, title, assignee_id: assigneeId, crm_step_id: isCrmContext ? crmStepId : null, duration_days, chain_mode }
              : r
          );
          const first = next.find((r) => r.order_index === 1);
          if (first && first.chain_mode !== 'sequential') {
            return renumberRows(
              next.map((r) => (r.id === first.id ? { ...r, chain_mode: 'sequential' } : r))
            );
          }
          return renumberRows(next);
        });
      } else {
        const id = `tmp-${Date.now()}`;
        setRows((prev) => {
          const newRow = {
            id,
            order_index: prev.length + 1,
            title,
            assignee_id: assigneeId,
            crm_step_id: isCrmContext ? crmStepId : null,
            duration_days,
            chain_mode,
          };
          return renumberRows([...prev, newRow]);
        });
      }
      setRowDialogVisible(false);
      setEditingRowId(null);
      resetForm({
        values: { title: '', assignee_id: '', crm_step_id: '', duration_days: 0, chain_mode: 'sequential' },
      });
    },
  });

  const openAddRow = useCallback(() => {
    setEditingRowId(null);
    rowFormik.resetForm({
      values: { title: '', assignee_id: '', crm_step_id: '', duration_days: 0, chain_mode: 'sequential' },
    });
    setRowDialogVisible(true);
  }, [rowFormik]);

  const openEditRow = useCallback(
    (rowData) => {
      setEditingRowId(rowData.id);
      rowFormik.setValues({
        title: rowData.title || '',
        assignee_id: rowData.assignee_id != null ? String(rowData.assignee_id) : '',
        crm_step_id: rowData.crm_step_id != null ? String(rowData.crm_step_id) : '',
        duration_days: rowData.duration_days ?? 0,
        chain_mode: rowData.chain_mode === 'parallel_prev' ? 'parallel_prev' : 'sequential',
      });
      setRowDialogVisible(true);
    },
    [rowFormik]
  );

  const editSelectedRow = useCallback(() => {
    const api = tableRef.current;
    const sel = api?.getSelectedData?.() || [];
    if (sel.length === 1) openEditRow(sel[0]);
  }, [openEditRow]);

  const formik = useFormik({
    initialValues:
      formTemplate == null
        ? { name: '' }
        : formType === 'edit'
          ? { id: formTemplate.id, name: formTemplate.name || '' }
          : { name: formTemplate.name || '' },
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      if (!values.name) errors.name = t('Common.RequiredField');
      if ((rows || []).length === 0) errors.rows = t('TaskTemplateAdd.AtLeastOneRow');
      return errors;
    },
    onSubmit: async (values) => {
      setDialogText(t('Common.Saving'));
      setVisible(true);

      const normalized = renumberRows(rows).map((r) => ({
        order_index: r.order_index,
        title: (r.title || '').trim(),
        assignee_id: r.assignee_id ?? null,
        ...(isCrmContext ? { crm_step_id: r.crm_step_id ?? null } : {}),
        duration_days: Number.isFinite(+r.duration_days) ? +r.duration_days : 0,
        chain_mode: r.chain_mode === 'parallel_prev' ? 'parallel_prev' : 'sequential',
      }));

      const fd = new FormData();
      fd.append('name', values.name);
      fd.append('template_context', normalizedContext);
      fd.append('rows_json', JSON.stringify(normalized));

      try {
        let createdId = null;
        if (formType === 'add') {
          const postRes = await postRequest('/api/task_templates', fd, true);
          createdId = postRes?.data?.id ?? null;
          if (!createdId) {
            setVisible(false);
            alert(t('ServiceTask.SaveFailed'));
            return;
          }
        } else {
          await postRequest(`/api/task_templates/${formik.values.id}`, fd, true);
        }

        const refresh = await getRequest(`/api/task_templates?context=${normalizedContext}`);
        const mergedTemplates = [
          ...(state.task_templates || []).filter((tpl) => (tpl.template_context || 'service') !== normalizedContext),
          ...(refresh.data || []),
        ];
        dispatch({ type: 'load_task_templates', newTaskTemplates: mergedTemplates });

        if (closeTab) dispatch({ type: 'close_tab', id: state.selectedTab });

        if (formType === 'add' && createdId != null) {
          const newest = refresh.data?.find((t) => t.id === createdId);
          if (newest) {
            setFormTemplate(newest);
            setFormType('edit');
            formik.setValues({ id: newest.id, name: newest.name });
          }
        }

        setVisible(false);
        toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('TaskTemplateAdd.TemplateSaved') });
      } catch (e) {
        setVisible(false);
        alert(t('ServiceTask.SaveFailed'));
      }
    },
  });

  useEffect(() => {
    if (!formTemplate) {
      setRows([]);
      return;
    }
    const safeRows = (formTemplate.rows_json || []).map((r, i) => ({
      id: r.id || `${formTemplate.id}-${i + 1}`,
      order_index: r.order_index ?? (i + 1),
      title: r.title || '',
      assignee_id: r.assignee_id ?? null,
      crm_step_id: r.crm_step_id ?? null,
      duration_days: Number.isFinite(+r.duration_days) ? +r.duration_days : 0,
      chain_mode: r.chain_mode === 'parallel_prev' ? 'parallel_prev' : 'sequential',
    }));
    setRows(safeRows);
  }, [formTemplate]);

  const confirmDeleteRows = useCallback(() => {
    const api = tableRef.current;
    const selected = api?.getSelectedData?.() || [];
    if (selected.length === 0) return;
    confirmDialog({
      message: t('ServiceTask.ConfirmDeleteRecord'),
      header: t('ServiceTask.ConfirmDeleteTitle'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: () => {
        const selectedIds = new Set(selected.map((r) => r.id));
        const filtered = renumberRows((rows || []).filter((r) => !selectedIds.has(r.id)));
        setRows(filtered);
        api?.deselectRow?.();
        setHasSelection(false);
        setEditRowDisabled(true);
        toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('TaskTemplateAdd.RowsRemoved'), life: 3000 });
      },
    });
  }, [rows, toast]);

  const onRowMoved = () => {
    if (!tableRef.current) return;
    const visual = tableRef.current.getData() || [];
    let reordered = renumberRows(visual);
    if (reordered[0] && reordered[0].chain_mode !== 'sequential') {
      reordered[0] = { ...reordered[0], chain_mode: 'sequential' };
      tableRef.current.updateData([{ id: reordered[0].id, chain_mode: 'sequential' }]);
    }
    tableRef.current.updateData(reordered.map((r) => ({ id: r.id, order_index: r.order_index })));
    setRows(reordered);
  };

  const placeholderHTML = (text) =>
    `<span class="tpl-placeholder">${text}</span>`;

  const multilineTitleFormatter = (cell) => {
    const v = (cell.getValue() ?? '').toString();
    if (!v.trim()) return placeholderHTML('—');
    const safe = v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<div class="tpl-multiline">${safe}</div>`;
  };

  const assigneeFormatter = (cell) => {
    const v = cell.getValue();
    if (v == null || v === '') return placeholderHTML('—');
    const opt = usersOptions.find((o) => String(o.value) === String(v));
    return opt ? opt.label : placeholderHTML('—');
  };

  const crmStepFormatter = (cell) => {
    const v = cell.getValue();
    if (v == null || v === '') return placeholderHTML('—');
    const opt = crmStepOptions.find((o) => String(o.value) === String(v));
    return opt ? opt.label : placeholderHTML('—');
  };

  /** Reťazenie – rovnako ako mntn001 (prime MultiSelect), CSV z viacerých volieb. */
  const chainModeHeaderFilterFunc = useCallback((headerValue, fieldVal) => {
    if (headerValue == null || String(headerValue).trim() === '') return true;
    const selected = new Set(
      String(headerValue)
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean)
    );
    return selected.has(String(fieldVal ?? ''));
  }, []);

  /** Viacnásobný výber používateľov (assignee_id) – hodnota filtra je "id1,id2,...". */
  const assigneeHeaderFilterFunc = useCallback((headerValue, rowValue) => {
    if (headerValue == null || String(headerValue).trim() === '') return true;
    const selected = new Set(
      String(headerValue)
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean)
    );
    if (rowValue == null || rowValue === '') return false;
    return selected.has(String(rowValue));
  }, []);

  const crmStepHeaderFilterFunc = useCallback((headerValue, rowValue) => {
    if (headerValue == null || String(headerValue).trim() === '') return true;
    const selected = new Set(
      String(headerValue)
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean)
    );
    if (rowValue == null || rowValue === '') return false;
    return selected.has(String(rowValue));
  }, []);

  const unitDays = getTimeUnitDays(state?.module_settings || []);

  const durationFormatter = (cell) => {
    const v = cell.getValue();
    if (v == null || v === '') return placeholderHTML(`0 ${unitDays}`);
    const n = Number(v);
    return Number.isFinite(n) ? `${n} ${unitDays}` : placeholderHTML(`0 ${unitDays}`);
  };

  /** Zobrazenie ID: `6-1` → `1`; dočasné `tmp-…` → poradie riadku (order_index). */
  const templateRowIdDisplayFormatter = useCallback((cell) => {
    const row = cell.getRow().getData();
    const id = row?.id;
    if (id == null || id === '') return placeholderHTML('—');
    const s = String(id);
    const composite = /^(\d+)-(\d+)$/.exec(s);
    if (composite) return composite[2];
    if (s.startsWith('tmp-')) return String(row.order_index ?? '');
    return s;
  }, []);

  const columns = useMemo(() => {
    function EditRowFormatterInner(props) {
      const rowData = props.cell.getRow().getData();
      return (
        <div className="flex items-center justify-center w-full h-full">
          <button type="button" onClick={() => openEditRow(rowData)} aria-label={t('TaskTemplateAdd.EditTemplateRow')}>
            <EditRowIcon />
          </button>
        </div>
      );
    }

    return [
      {
        formatter: 'rowSelection',
        titleFormatter: 'rowSelection',
        hozAlign: 'center',
        width: 44,
        headerSort: false,
        headerFilter: false,
        resizable: false,
        frozen: true,
      },
      {
        title: '',
        field: 'editor',
        hozAlign: 'center',
        width: 44,
        minWidth: 44,
        headerSort: false,
        headerFilter: false,
        resizable: false,
        frozen: true,
        formatter: reactFormatter(<EditRowFormatterInner />),
      },
      {
        title: t('ServiceTaskTable.Col.Id'),
        field: 'id',
        width: 70,
        widthGrow: 0,
        hozAlign: 'left',
        headerSort: false,
        headerFilter: false,
        resizable: false,
        formatter: templateRowIdDisplayFormatter,
        visible: rowColumnsVisible.id !== false,
      },
      {
        title: t('TaskTemplateAdd.RowName'),
        field: 'title',
        headerFilter: 'input',
        formatter: multilineTitleFormatter,
        minWidth: 220,
        widthGrow: 2,
        variableHeight: true,
        cssClass: 'tpl-cell',
        visible: rowColumnsVisible.title !== false,
      },
      {
        title: t('TaskTemplateAdd.Responsible'),
        field: 'assignee_id',
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: {
          placeholder: t('Common.All'),
          valueType: 'number',
          options: usersOptions,
        },
        headerFilterFunc: assigneeHeaderFilterFunc,
        formatter: assigneeFormatter,
        minWidth: 180,
        widthGrow: 1,
        cssClass: 'tpl-cell',
        visible: rowColumnsVisible.assignee_id !== false,
      },
      ...(isCrmContext ? [{
        title: t('TaskTemplateAdd.Step'),
        field: 'crm_step_id',
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: {
          placeholder: t('Common.All'),
          valueType: 'number',
          options: crmStepOptions,
        },
        headerFilterFunc: crmStepHeaderFilterFunc,
        formatter: crmStepFormatter,
        minWidth: 180,
        widthGrow: 1,
        cssClass: 'tpl-cell',
        visible: rowColumnsVisible.crm_step_id !== false,
      }] : []),
      {
        title: `${t('TaskTemplateAdd.Duration')} (${unitDays})`,
        field: 'duration_days',
        headerFilter: 'number',
        hozAlign: 'left',
        minWidth: 120,
        widthGrow: 0,
        formatter: durationFormatter,
        cssClass: 'tpl-cell tight',
        visible: rowColumnsVisible.duration_days !== false,
      },
      {
        title: t('TaskTemplateAdd.ChainMode'),
        field: 'chain_mode',
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: {
          placeholder: t('Common.All'),
          valueType: 'string',
          options: [
            { label: t('TaskTemplateAdd.Chain.Sequential'), value: 'sequential' },
            { label: t('TaskTemplateAdd.Chain.Parallel'), value: 'parallel_prev' },
          ],
        },
        headerFilterFunc: chainModeHeaderFilterFunc,
        hozAlign: 'left',
        minWidth: 132,
        maxWidth: 176,
        widthGrow: 0,
        formatter: (cell) => {
          const row = cell.getRow().getData();
          const isFirst = Number(row.order_index) === 1;
          if (isFirst) {
            const seq = t('TaskTemplateAdd.Chain.Sequential');
            return `<span class="text-gray-400">${seq}&nbsp;<i class="pi pi-lock" style="font-size:0.9em"></i></span>`;
          }
          return cell.getValue() === 'parallel_prev' ? t('TaskTemplateAdd.Chain.Parallel') : t('TaskTemplateAdd.Chain.Sequential');
        },
        cssClass: 'tpl-cell',
        visible: rowColumnsVisible.chain_mode !== false,
      },
    ];
  }, [t, usersOptions, crmStepOptions, unitDays, chainModeHeaderFilterFunc, assigneeHeaderFilterFunc, crmStepHeaderFilterFunc, templateRowIdDisplayFormatter, rowColumnsVisible, openEditRow, isCrmContext]);

  const closeMenu = useCallback(() => {
    OverflowMenuProvider.setVisible(null);
    setExpandedKeys({});
  }, []);

  const openMaskDialog = useCallback(() => {
    setMaskVisible(true);
    closeMenu();
  }, [closeMenu]);

  const saveRowMaskColumns = useCallback(() => {
    const normalized = { ...rowColumnDefaults, ...rowColumnsVisible };
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Mask'), detail: t('ServiceTask.SelectAtLeastOneColumn'), life: 4000 });
      return;
    }
    const filtersSnapshot = tableRef.current?.getHeaderFilters?.() ?? [];
    setUserMaskFilter(pageMask, 'all', normalized, filtersSnapshot, null, null);
    setRowColumnsVisible(normalized);
    setMaskVisible(false);
  }, [pageMask, rowColumnDefaults, rowColumnsVisible, setUserMaskFilter, toast]);

  const setRowMaskField = useCallback(
    (field, value) => {
      if (!Object.prototype.hasOwnProperty.call(rowColumnDefaults, field)) return;
      const table = tableRef.current;
      const next = { ...rowColumnDefaults, ...rowColumnsVisible, [field]: value };
      if (value === false && Object.keys(rowColumnDefaults).filter((k) => next[k] !== false).length < 1) {
        toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Mask'), detail: t('ServiceTask.AtLeastOneColumnMustRemainVisible'), life: 3500 });
        return;
      }
      applyRowDataColumnVisibilityToTable(table, next);
      setRowColumnsVisible(next);
    },
    [rowColumnDefaults, rowColumnsVisible, toast]
  );

  const maskRowSelectAll = useCallback(() => {
    const next = { ...rowColumnDefaults };
    Object.keys(next).forEach((k) => { next[k] = true; });
    applyRowDataColumnVisibilityToTable(tableRef.current, next);
    setRowColumnsVisible(next);
  }, [rowColumnDefaults]);

  const maskRowDeselectAll = useCallback(() => {
    const next = { ...rowColumnDefaults };
    Object.keys(next).forEach((k) => { next[k] = false; });
    next.title = true;
    applyRowDataColumnVisibilityToTable(tableRef.current, next);
    setRowColumnsVisible(next);
  }, [rowColumnDefaults]);

  const itemRenderer = (item, options) => (
    <button
      type="button"
      className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left"
      onClick={options.onClick}
    >
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const panelItems = useMemo(() => ([
    {
      key: '0',
      label: t('Common.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: [
        {
          key: '0_1',
          label: t('Common.SetFilter'),
          icon: 'pi pi-filter',
          command: () => {
            setUserMaskFilter(pageMask, 'set_filters', null, tableRef.current?.getHeaderFilters?.() ?? [], null);
            closeMenu();
          },
          template: itemRenderer,
        },
        {
          key: '0_2',
          label: t('Common.ClearFilter'),
          icon: 'pi pi-filter-slash',
          command: () => {
            setUserMaskFilter(pageMask, 'set_filters', null, [], null);
            tableRef.current?.clearHeaderFilter?.();
            closeMenu();
          },
          template: itemRenderer,
        },
        {
          key: '0_3',
          label: t('Common.SetSorting'),
          icon: 'pi pi-sort-alt',
          command: () => {
            setUserMaskFilter(pageMask, 'set_sorters', null, null, null, cleanSorters(tableRef.current?.getSorters?.() ?? []));
            closeMenu();
          },
          template: itemRenderer,
        },
        {
          key: '0_4',
          label: t('Common.ClearSorting'),
          icon: 'pi pi-sort-alt-slash',
          command: () => {
            setUserMaskFilter(pageMask, 'set_sorters', null, null, null, []);
            tableRef.current?.setSort?.([]);
            closeMenu();
          },
          template: itemRenderer,
        },
        {
          key: '0_5',
          label: t('Common.SetMask'),
          icon: 'pi pi-server',
          command: openMaskDialog,
          template: itemRenderer,
        },
        {
          key: '0_6',
          label: t('Common.Notifications'),
          icon: 'pi pi-envelope',
          command: () => { openChosenNotificationPreferences(pageMask); closeMenu(); },
          template: itemRenderer,
        },
      ],
    },
  ]), [closeMenu, openChosenNotificationPreferences, openMaskDialog, pageMask, setUserMaskFilter]);

  const handleClickOutside = useCallback((event) => {
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      setExpandedKeys({});
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [handleClickOutside]);

  const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = createArrayNavigateHandlers(
    (state.task_templates || []).filter((t) => (t.template_context || 'service') === normalizedContext),
    formik.values?.id,
    setFormTemplate
  );

  const templateId = formik.values?.id;

  const buildDetailExportQueryString = useCallback((opts = {}) => {
    if (!templateId) return '';
    const params = {
      only_id: String(templateId),
      sorters: JSON.stringify([]),
    };
    return buildExportQueryStringFromParams(params, opts);
  }, [templateId]);

  const runTemplatePrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText(t('Common.PreparingPrint'));
      setVisible(true);
      const suffix = buildDetailExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: LIST_EXPORT_COLUMN_FIELDS,
      });
      const html = await fetchTextGet(`/api/task_templates/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) {
        toast?.current?.show({
          severity: 'warn',
          summary: t('Common.Print'),
          detail: t('Common.EnablePopups'),
          life: 4000,
        });
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Print'), detail: e?.message || t('ServiceTask.PrintFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildDetailExportQueryString, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, toast]);

  const exportTemplateXlsx = useCallback(async () => {
    try {
      setDialogText(t('Common.PreparingExport'));
      setVisible(true);
      const suffix = buildDetailExportQueryString({
        includePrintColumns: true,
        printColumns: LIST_EXPORT_COLUMN_FIELDS,
      });
      const defaultXlsx = xlsxDownloadFilename(t(EXPORT_DOCUMENT_TITLE_KEYS.TASK_TEMPLATES));
      const { blob, filename } = await downloadBlobGet(`/api/task_templates/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Export'), detail: e?.message || t('ServiceTask.ExportFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildDetailExportQueryString, setDialogText, setVisible, toast]);

  const reloadTemplateDetail = useCallback(async () => {
    if (!templateId) return;
    setDialogText(t('Common.Refresh'));
    setVisible(true);
    try {
      const r = await getRequest(`/api/task_templates/${templateId}`);
      const templateData = r?.data ?? r;
      if (templateData) {
        setFormTemplate(templateData);
        formik.setValues({ id: templateData.id, name: templateData.name || '' });
        const refresh = await getRequest(`/api/task_templates?context=${normalizedContext}`);
        const mergedTemplates = [
          ...(state.task_templates || []).filter((tpl) => (tpl.template_context || 'service') !== normalizedContext),
          ...(refresh.data || []),
        ];
        dispatch({ type: 'load_task_templates', newTaskTemplates: mergedTemplates });
        toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('TaskTemplateAdd.DataRefreshed'), life: 3000 });
      }
    } catch (e) {
      toast?.current?.show({
        severity: 'error',
        summary: t('Common.Refresh'),
        detail: e?.message || t('TaskTemplateAdd.LoadFailed'),
        life: 4000,
      });
    } finally {
      setVisible(false);
    }
  }, [templateId, dispatch, formik, setDialogText, setVisible, toast]);

  const editingWillBeFirst =
    editingRowId != null
      ? rows.some((r) => r.id === editingRowId && r.order_index === 1)
      : rows.length === 0;

  return (
    <>
      <style>{`
        .task-template-rows .tabulator .tabulator-row .tabulator-cell {
          padding-top: 4px;
          padding-bottom: 4px;
        }
        .task-template-rows .tabulator-col[tabulator-field="title"] .tabulator-header-filter input:focus,
        .task-template-rows .tabulator-col[tabulator-field="title"] .tabulator-header-filter input:focus-visible {
          outline: none !important;
          box-shadow: none !important;
        }
      `}</style>
      <div>
        <div className="w-full h-10 flex flex-row bg-[#333]">
          <IntersectionObserverWrapper>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button type="button" onClick={() => { setCloseTab(true); submitRef.current?.click(); }}>
                <SaveAndCloseIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button type="button" onClick={() => submitRef.current?.click()}>
                <SaveIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button type="button" onClick={openAddRow} title={t('TaskTemplateAdd.AddTemplateRow')}>
                <AddIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button
                type="button"
                className="disabled:opacity-25"
                disabled={editRowDisabled}
                onClick={editSelectedRow}
                title={t('TaskTemplateAdd.EditTemplateRow')}
              >
                <EditIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button
                type="button"
                onClick={confirmDeleteRows}
                disabled={!hasSelection}
                className={`transition-opacity ${!hasSelection ? 'opacity-25 cursor-not-allowed' : ''}`}
                title={!hasSelection ? t('TaskTemplateAdd.SelectRowToDelete') : t('TaskTemplateAdd.DeleteSelectedRows')}
              >
                <DeleteIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button
                type="button"
                title={t('Common.Print')}
                disabled={!templateId}
                className="disabled:opacity-25"
                onClick={() => setPrintChoiceVisible(true)}
              >
                <PrintIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button
                type="button"
                title={t('Common.ExportToExcel')}
                disabled={!templateId}
                className="disabled:opacity-25"
                onClick={exportTemplateXlsx}
              >
                <ExportIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button
                type="button"
                title={t('Common.Refresh')}
                disabled={!templateId}
                className="disabled:opacity-25"
                onClick={reloadTemplateDetail}
              >
                <RefreshIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button type="button" className="disabled:opacity-25" disabled={!formik.values.id} onClick={scrollToFirst}>
                <BackToFirstIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button type="button" className="disabled:opacity-25" disabled={!formik.values.id} onClick={scrollUp}>
                <BackIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button type="button" className="disabled:opacity-25" disabled={!formik.values.id} onClick={scrollDown}>
                <ForwardIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button type="button" className="disabled:opacity-25" disabled={!formik.values.id} onClick={scrollToLast}>
                <ForwardToLastIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button type="button" onClick={() => openFavoriteDialog(t('TaskTemplate.PlannerTemplates'), pageMask)}>
                <FavoriteIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button type="button" onClick={() => setFastView(pageMask, rowColumnsVisible, columns)}>
                <PriceIcon />
              </button>
            </div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
            <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
              <PanelMenu model={panelItems} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
            </div>
          </IntersectionObserverWrapper>
        </div>

        <form onSubmit={formik.handleSubmit} className="w-full">
          <Splitter
            className="h-[calc(100vh_-_177px)]"
            layout="vertical"
            pt={{ gutter: { className: 'bg-[#333] h-8' }, gutterHandler: { className: 'bg-secondary ' } }}
          >
            <SplitterPanel className="flex align-items-center justify-content-center overflow-y-auto min-h-0" minSize={5} size={28}>
              <div className="relative flex flex-col overflow-x w-full">
                <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
                  <h1 className="text-3xl font-semibold text-left text-zinc-900">
                    {formType === 'add' ? t('TaskTemplate.NewTemplate') : t('TaskTemplate.EditTemplate')}
                  </h1>
                  {formType === 'edit' && formik.values.id != null && (
                    <label className="block text-md font-semibold text-gray-800 mt-1">ID: {formik.values.id}</label>
                  )}

                  <div className="mt-4 mb-2">
                    <label htmlFor="task-template-name" className="block text-md font-semibold text-gray-800">{t('TaskTemplateAdd.TemplateName')}</label>
                    <input
                      id="task-template-name"
                      name="name"
                      required
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                      placeholder={t('TaskTemplateAdd.TemplateName')}
                    />
                    {formik.errors.name && <div className="text-red-600 text-sm mt-1">{formik.errors.name}</div>}
                  </div>
                </div>
              </div>
            </SplitterPanel>
            <SplitterPanel
              className="flex flex-col align-items-center justify-content-center w-full overflow-hidden min-h-0"
              size={72}
              style={{ minHeight: '110px' }}
            >
              <div className="flex flex-col w-full h-full min-h-0 px-2 pb-2">
                <h3 className="text-lg font-semibold text-zinc-900 px-1 pt-2 shrink-0">{t('TaskTemplate.RowCount')}</h3>
                <ReactTabulator
                  data={rows}
                  columns={columns}
                  options={{
                    layout: 'fitColumns',
                    autoResize: true,
                    renderHorizontal: 'basic',
                    reactiveData: false,
                    movableRows: true,
                    movableColumns: true,
                    clipboard: true,
                    textDirection: 'ltr',
                    variableHeight: true,
                    filterMode: 'local',
                  }}
                  events={{
                    rowMoved: onRowMoved,
                    rowSelectionChanged: (data) => {
                      const n = data?.length ?? 0;
                      setHasSelection(n > 0);
                      setEditRowDisabled(n !== 1);
                    },
                    tableBuilt: () => {
                      isRestoringRef.current = true;
                      const mask = (state.user_mask_filters || []).filter((m) => m.page === pageMask);
                      const safeParse = (v, fallback) => {
                        if (v === null || v === undefined) return fallback;
                        if (typeof v === 'string') {
                          try { return JSON.parse(v); } catch { return fallback; }
                        }
                        return v;
                      };
                      if (mask.length > 0) {
                        const attributes = safeParse(mask[0].attributes, []);
                        const filters = safeParse(mask[0].filters, []);
                        const sorters = safeParse(mask[0].sorters, []);
                        const json_columns = safeParse(mask[0].columns, {});
                        const tbl = tableRef.current;
                        if (tbl) {
                          if (Array.isArray(attributes) && attributes.length > 1) {
                            tbl.setColumnLayout?.(attributes);
                          }
                          if (Array.isArray(filters)) {
                            filters.forEach((f) => tbl.setHeaderFilterValue?.(f.field, f.value));
                          }
                          if (Array.isArray(sorters) && sorters.length > 0) {
                            tbl.setSort?.(sorters);
                          }
                          if (json_columns && typeof json_columns === 'object') {
                            const merged = { ...rowColumnDefaults, ...(json_columns && typeof json_columns === 'object' ? json_columns : {}) };
                            setRowColumnsVisible(merged);
                            applyRowDataColumnVisibilityToTable(tbl, merged);
                          }
                        }
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    },
                    ...getTabulatorPersistLayoutEvents(pageMask, tableRef, setUserMaskFilter),
                    headerFilterChanged: () => {
                      if (!isRestoringRef.current) setUserMaskFilter(pageMask, 'set_filters', null, tableRef.current?.getHeaderFilters?.() ?? [], null);
                    },
                    sortChanged: () => {
                      if (!isRestoringRef.current) {
                        setUserMaskFilter(pageMask, 'set_sorters', null, null, null, cleanSorters(tableRef.current?.getSorters?.() ?? []));
                      }
                    },
                  }}
                  onRef={(r) => { tableRef.current = r?.current; }}
                  className="task-template-rows flex-1 min-h-0 w-full [&_.tabulator]:min-h-[200px] [&_.tabulator-row]:!h-auto [&_.tabulator-row]:!min-h-[32px] [&_.tabulator-cell]:align-top"
                />
                {formik.errors.rows && (
                  <div className="text-red-600 text-sm mt-1 px-1 shrink-0">{formik.errors.rows}</div>
                )}
              </div>
            </SplitterPanel>
          </Splitter>
          <button type="submit" ref={submitRef} className="hidden" />
        </form>
      </div>

      <Dialog
        header={editingRowId ? t('TaskTemplateAdd.EditTemplateRow') : t('TaskTemplateAdd.AddTemplateRow')}
        visible={rowDialogVisible}
        style={{ width: 'min(90vw, 520px)' }}
        onHide={() => {
          setRowDialogVisible(false);
          setEditingRowId(null);
          rowFormik.resetForm();
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            rowFormik.handleSubmit();
          }}
          className="flex flex-col gap-4"
        >
          <div>
            <label htmlFor="row-title" className="block text-md font-semibold text-gray-800">{t('CrmCommunication.Tasks.TitlePlaceholder')}</label>
            <textarea
              id="row-title"
              name="title"
              rows={4}
              value={rowFormik.values.title}
              onChange={rowFormik.handleChange}
              className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
              placeholder={t('TaskTemplateAdd.RowDescriptionPlaceholder')}
            />
            {rowFormik.errors.title && <div className="text-red-600 text-sm mt-1">{rowFormik.errors.title}</div>}
          </div>
          <div>
            <label htmlFor="row-assignee" className="block text-md font-semibold text-gray-800">{t('Common.ResponsibleShort')}</label>
            <select
              id="row-assignee"
              name="assignee_id"
              value={rowFormik.values.assignee_id}
              onChange={rowFormik.handleChange}
              className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            >
              <option value="">—</option>
              {usersOptions.map((o) => (
                <option key={o.value} value={String(o.value)}>{o.label}</option>
              ))}
            </select>
          </div>
          {isCrmContext && (
            <div>
              <label htmlFor="row-crm-step" className="block text-md font-semibold text-gray-800">{t('TaskTemplateAdd.Step')}</label>
              <select
                id="row-crm-step"
                name="crm_step_id"
                value={rowFormik.values.crm_step_id}
                onChange={rowFormik.handleChange}
                className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
              >
                <option value="">—</option>
                {crmStepOptions.map((o) => (
                  <option key={o.value} value={String(o.value)}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label htmlFor="row-duration" className="block text-md font-semibold text-gray-800">{`${t('TaskTemplateAdd.Duration')} (${unitDays})`}</label>
            <input
              id="row-duration"
              name="duration_days"
              type="number"
              min={0}
              step={1}
              value={rowFormik.values.duration_days}
              onChange={rowFormik.handleChange}
              className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            />
            {rowFormik.errors.duration_days && <div className="text-red-600 text-sm mt-1">{rowFormik.errors.duration_days}</div>}
          </div>
          <div>
            <label htmlFor="row-chain" className="block text-md font-semibold text-gray-800">{t('ServiceTaskPlanner.Chaining')}</label>
            <select
              id="row-chain"
              name="chain_mode"
              value={rowFormik.values.chain_mode}
              onChange={rowFormik.handleChange}
              disabled={editingWillBeFirst}
              className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40 disabled:opacity-60"
            >
              <option value="sequential">Po jednom</option>
              <option value="parallel_prev">Paralelne s predchádzajúcim blokom</option>
            </select>
            {editingWillBeFirst && (
              <p className="text-sm text-gray-600 mt-1">{t('TaskTemplateAdd.FirstRowAlwaysSequential')}</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="px-4 py-2 bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300"
              onClick={() => {
                setRowDialogVisible(false);
                setEditingRowId(null);
                rowFormik.resetForm();
              }}
            >
              {t('Common.Cancel')}
            </button>
            <button type="submit" className="px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-zinc-800">
              {t('Common.Save')}
            </button>
          </div>
        </form>
      </Dialog>

      <TabulatorMaskDialog
        visible={maskVisible}
        onHide={() => setMaskVisible(false)}
        title={t('TaskTemplateAdd.SetMaskRows')}
        fields={Object.keys(rowColumnDefaults)}
        checkedMap={rowColumnsVisible}
        onToggleField={setRowMaskField}
        onSelectAll={maskRowSelectAll}
        onDeselectAll={maskRowDeselectAll}
        onSave={saveRowMaskColumns}
        selectAllLabel={t('Common.SelectAll')}
        deselectAllLabel={t('TaskTemplateAdd.DeselectAllExceptRowName')}
        saveLabel={t('Common.Save')}
        getFieldLabel={(field) => {
          const labels = {
            id: t('ServiceTaskTable.Col.Id'),
            title: t('TaskTemplateAdd.RowName'),
            assignee_id: t('TaskTemplateAdd.Responsible'),
            crm_step_id: t('TaskTemplateAdd.Step'),
            duration_days: `${t('TaskTemplateAdd.Duration')} (${unitDays})`,
            chain_mode: t('TaskTemplateAdd.ChainMode'),
          };
          return labels[field] || field;
        }}
        inputIdPrefix="tt-row-mask"
      />

      <PrintOptionsDialog
        visible={printChoiceVisible}
        onHide={() => setPrintChoiceVisible(false)}
        orientation={printOrientationChoice}
        onOrientationChange={setPrintOrientationChoice}
        layout={printLayoutChoice}
        onLayoutChange={setPrintLayoutChoice}
        onConfirm={runTemplatePrint}
      />
    </>
  );
}
